import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/lib/format";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const SORTS = [
  ["featured", "Featured"],
  ["bestseller", "Top Rated"],
  ["newest", "New Arrivals"],
  ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"],
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], fabrics: [], occasions: [], colors: [], sizes: [] });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sel, setSel] = useState({ category: [], fabric: [], occasion: [], color: [], size: [] });
  const [sort, setSort] = useState("featured");
  const [params] = useSearchParams();

  useSEO({
    title: "Shop Co-ord Sets for Women | Just Kords",
    description: "Browse all Just Kords co-ord sets — resort linen, power blazers, satin evening sets, fine knits and weekend casuals. Filter by fabric, fit, colour and occasion.",
  });

  useEffect(() => {
    const cat = params.get("category");
    const fab = params.get("fabric");
    if (cat || fab) setSel((s) => ({ ...s, category: cat ? [cat] : s.category, fabric: fab ? [fab] : s.fabric }));
  }, [params]);

  useEffect(() => {
    axios.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/products/facets`),
    ]).then(axios.spread((p, f) => {
      setProducts(p.data);
      setFacets(f.data);
      setLoading(false);
    })).catch(() => setLoading(false));
  }, []);

  const toggle = (group, value) =>
    setSel((s) => ({ ...s, [group]: s[group].includes(value) ? s[group].filter((v) => v !== value) : [...s[group], value] }));

  const clearAll = () => setSel({ category: [], fabric: [], occasion: [], color: [], size: [] });
  const activeCount = Object.values(sel).reduce((n, arr) => n + arr.length, 0);

  const visible = useMemo(() => {
    let list = products.filter((p) =>
      (!sel.category.length || sel.category.includes(p.category)) &&
      (!sel.fabric.length || sel.fabric.includes(p.fabric)) &&
      (!sel.occasion.length || sel.occasion.includes(p.occasion)) &&
      (!sel.color.length || (p.colors || []).some((c) => sel.color.includes(c))) &&
      (!sel.size.length || (p.sizes || []).some((v) => sel.size.includes(v.size) && v.stock > 0))
    );
    switch (sort) {
      case "price_asc": list = [...list].sort((a, b) => a.price_inr - b.price_inr); break;
      case "price_desc": list = [...list].sort((a, b) => b.price_inr - a.price_inr); break;
      case "newest": list = [...list].reverse(); break;
      case "bestseller": list = [...list].sort((a, b) => b.rating - a.rating); break;
      default: list = [...list].sort((a, b) => (b.featured - a.featured) || (b.rating - a.rating));
    }
    return list;
  }, [products, sel, sort]);

  const FILTER_GROUPS = [
    ["Category", "category", facets.categories],
    ["Fabric", "fabric", facets.fabrics],
    ["Occasion", "occasion", facets.occasions],
    ["Colour", "color", facets.colors],
    ["Available Size", "size", facets.sizes],
  ];

  const sidebar = (
    <div className="space-y-8" data-testid="shop-filters">
      {FILTER_GROUPS.filter(([, key, values]) => (values || []).length > 0).map(([label, key, values]) => (
        <div key={key}>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">{label}</h4>
          <div className="mt-3 flex flex-wrap gap-2">
            {(values || []).map((v) => (
              <button
                key={v}
                data-testid={`filter-${key}-${v.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggle(key, v)}
                className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors duration-200 ${
                  sel[key].includes(v) ? "border-crimson bg-crimson text-white" : "border-linec bg-white text-mutedc hover:border-ink hover:text-ink"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="border-b border-linec pb-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">The Edit</p>
          <h1 className="mt-4 font-serif text-5xl font-light sm:text-6xl">Shop Co-ords</h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-mutedc">
            Every set is a complete look in one purchase. Filter by mood, fabric or fit.
          </p>
        </Reveal>

        <div className="flex items-center justify-between gap-4 py-6">
          <div className="flex items-center gap-3">
            <button
              data-testid="shop-mobile-filters-button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-2 rounded-full border border-ink px-5 py-2 text-sm font-semibold lg:hidden"
            >
              <SlidersHorizontal size={15} /> Filters {activeCount > 0 && `(${activeCount})`}
            </button>
            <p data-testid="shop-result-count" className="font-mono text-[11px] uppercase tracking-widest text-mutedc">
              {loading ? "Loading" : `${visible.length} sets`}
            </p>
          </div>
          <select
            data-testid="shop-sort-select"
            value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-linec bg-white px-4 py-2 text-sm focus:border-crimson focus:outline-none"
          >
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {activeCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 pb-6" data-testid="active-filter-pills">
            {Object.entries(sel).flatMap(([k, arr]) => arr.map((v) => (
              <button key={`${k}-${v}`} onClick={() => toggle(k, v)} className="flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 text-xs text-white">
                {v} <X size={12} />
              </button>
            )))}
            <button onClick={clearAll} data-testid="shop-clear-filters" className="text-xs font-semibold uppercase tracking-widest text-crimson underline underline-offset-4">
              Clear all
            </button>
          </div>
        )}

        <div className="flex gap-10 pb-20">
          <AnimatePresence>
            {filtersOpen && (
              <motion.aside
                initial={{ width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }} exit={{ width: 0, opacity: 0 }}
                className="w-64 flex-shrink-0 overflow-hidden lg:hidden"
              >
                {sidebar}
              </motion.aside>
            )}
          </AnimatePresence>
          <aside className="hidden w-64 flex-shrink-0 lg:block">
            <div className="sticky top-36">{sidebar}</div>
          </aside>

          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-3" data-testid="shop-skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-linec" />
                    <div className="mt-4 h-4 w-3/4 bg-linec" />
                    <div className="mt-2 h-3 w-1/2 bg-linec" />
                  </div>
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div className="flex flex-col items-start gap-4 py-24" data-testid="shop-empty">
                <p className="font-serif text-3xl">Nothing matches — yet.</p>
                <p className="text-sm text-mutedc">Try removing a filter or two, or browse the full collection.</p>
                <button onClick={clearAll} className="rounded-full bg-crimson px-6 py-3 text-sm font-semibold text-white">Clear filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-3" data-testid="shop-grid">
                {visible.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
